import React, { useState } from "react";
import useTransactionListLogic, { Transaction } from "./Transactions.logic";
import { data, Link } from "react-router-dom";
import {
  DataTable,
  DataTableExpandedRows,
  DataTableFilterMeta,
  DataTableValueArray,
} from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { FilterMatchMode } from "primereact/api";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { Tooltip } from "primereact/tooltip";
import { Dialog } from "primereact/dialog";
import { classNames } from "primereact/utils";
import { formatDate } from "../../utils/dateUtils";
import QRCode from "qrcode";

const TransactionList: React.FC = () => {
  const {
    transactions,
    loading,
    paymentReceiptLoading,
    handleShowPaymentReceipt,
    resendEmailLoading,
    handleResendRegistrationEmail,
    checkIsEventCancellationAllowed,
    handleCancelAttendee,
    cancelLoading,
  } = useTransactionListLogic();

  const statusBodyTemplate = (rowData: Transaction) => {
    return rowData.status == "registered" ? (
      <span className="p-tag p-tag-success">{rowData.status_label}</span>
    ) : (
      <span className="p-tag p-tag-danger">{rowData.status_label}</span>
    );
  };

  const amountBodyTemplate = (rowData: Transaction) => {
    return (
      <div>
        <div>
          {rowData.amount > 0 ? (
            <>
              {(rowData.amount ?? 0).toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </>
          ) : (
            "Free"
          )}
        </div>
        {rowData.discount_amount > 0 && (
          <>
            <small className="text-muted">
              Discount:{" "}
              {rowData.discount_amount.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </small>
            <br />
          </>
        )}
        {rowData.donation_amount > 0 && (
          <>
            <small className="text-muted">
              Donation:{" "}
              {rowData.donation_amount.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </small>
            <br />
          </>
        )}
        {rowData.service_amount > 0 && (
          <>
            <small className="text-muted">
              Service Fee:{" "}
              {rowData.service_amount.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </small>
            <br />
          </>
        )}
        {rowData.payment_process_amount > 0 && (
          <>
            <small className="text-muted">
              Payment Process Fee:{" "}
              {rowData.payment_process_amount.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </small>
          </>
        )}
      </div>
    );
  };

  const [globalFilterValue, setGlobalFilterValue] = useState<string>("");

  const [filters, setFilters] = useState<DataTableFilterMeta>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
  });

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    let _filters = { ...filters };

    // @ts-ignore
    _filters["global"].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const renderHeader = () => {
    return (
      <>
        <ConfirmPopup />
        <div className="flex justify-content-start align-items-center mb-2 flex-column-responsive">
          <IconField iconPosition="left">
            <InputIcon className="fas fa-search" />
            <InputText
              value={globalFilterValue}
              onChange={onGlobalFilterChange}
              placeholder="Keyword Search"
            />
          </IconField>
        </div>
      </>
    );
  };

  const header = renderHeader();

  const [expandedRows, setExpandedRows] = useState<
    DataTableExpandedRows | DataTableValueArray | undefined
  >(undefined);

  const allowExpansion = (rowData: Transaction) => {
    // return rowData.payment_id !== null;
    return true;
  };

  const rowExpansionTemplate = (rowData: Transaction) => {
    return (
      <div className="container-fluid py-3 row-expansion-template">
        <div className="row">
          <div className="col-12 col-md-4 mb-3 mb-md-0">
            <h3 className="fw-bold">Payment Ref.:</h3>
            <p className="mb-2">{rowData.payment_id || "N/A"}</p>
          </div>
          {rowData.refund_id && (
            <div className="col-12 col-md-4 mb-3 mb-md-0">
              <h3 className="fw-bold">Refund Ref.:</h3>
              <p className="mb-2">{rowData.refund_id}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const eventBodyTemplate = (rowData: Transaction) => {
    return (
      <div>
        <div>
          <strong>{rowData.Event.title}</strong>
        </div>
        <small className="text-muted">
          {rowData.number_of_attendees}{" "}
          {rowData.number_of_attendees > 1 ? "Attendees" : "Attendee"}
        </small>
        <br />
        <small className="text-muted">
          {formatDate(rowData.Event.datetime)} | {rowData.Event.location_city}
        </small>
      </div>
    );
  };

  const [qrVisible, setQrVisible] = useState(false);
  const [qrImage, setQrImage] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);

  const handleGenerateQR = async (token: string, eventTitle: string) => {
    const data = JSON.stringify({ booking_token: token });
    try {
      const qrDataUrl = await QRCode.toDataURL(data, {
        width: 300,
        margin: 2,
      });
      setQrImage(qrDataUrl);
      setSelectedEvent(eventTitle);
      setQrVisible(true);
    } catch (err) {
      console.error("Error generating QR:", err);
    }
  };

  const qrBodyTemplate = (rowData: Transaction) => {
    return rowData.registration_token && rowData.status === "registered" ? (
      <Button
        icon="pi pi-qrcode"
        rounded
        tooltip="View QR Code"
        tooltipOptions={{ position: "top" }}
        onClick={() =>
          handleGenerateQR(rowData.registration_token, rowData.Event.title)
        }
      />
    ) : null;
  };

  const actionBodyTemplate = (rowData: Transaction) => {
    return (
      <>
        {rowData.status === "registered" && (
          <Button
            icon={
              resendEmailLoading ? "fas fa-spinner fa-spin" : "fas fa-envelope"
            }
            rounded
            onClick={() =>
              handleResendRegistrationEmail(rowData.Event.id, rowData.id)
            }
            tooltip={
              resendEmailLoading
                ? "Sending..."
                : "Resend Registration Confirmation Email"
            }
            tooltipOptions={{ position: "top" }}
            disabled={resendEmailLoading}
          ></Button>
        )}
        {rowData.amount > 0 && (
          <Button
            icon={
              paymentReceiptLoading
                ? "fas fa-spinner fa-spin"
                : "fas fa-receipt"
            }
            rounded
            onClick={() =>
              handleShowPaymentReceipt(
                rowData.Event.id ?? 0,
                rowData.id ?? null,
              )
            }
            tooltip={
              paymentReceiptLoading ? "Fetching..." : "View Payment Receipt"
            }
            tooltipOptions={{ position: "top" }}
            disabled={paymentReceiptLoading}
          ></Button>
        )}
        {checkIsEventCancellationAllowed(rowData.Event.datetime) &&
          rowData.status === "registered" && (
            <Button
              icon={cancelLoading ? "fas fa-spinner fa-spin" : "fas fa-times"}
              rounded
              onClick={(event) =>
                cancelAttendeeConfirm(
                  event,
                  rowData.Event.id ?? null,
                  rowData.id ?? null,
                )
              }
              tooltip={cancelLoading ? "Cancelling..." : "Cancel Booking"}
              tooltipOptions={{ position: "top" }}
              disabled={cancelLoading}
            ></Button>
          )}
      </>
    );
  };

  const cancelAttendeeConfirm = (
    event: any,
    eventId: number | null,
    registrationId: number | null,
  ) => {
    confirmPopup({
      target: event.currentTarget,
      message: "Do you want to cancel your registration as a attendee?",
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      accept: () => {
        handleCancelAttendee(eventId, registrationId);
      },
    });
  };

  return (
    <>
      <section className="schedule-three ngo_list_wrapper">
        <div className="container">
          <div className="schedule-three__top">
            <div
              className="section-title text-center wow fadeInLeft"
              data-wow-delay="100ms"
            >
              <h2 className="section-title__title">Your Bookings</h2>
            </div>
          </div>
          <div className="schedule-three__bottom">
            <DataTable
              value={transactions}
              expandedRows={expandedRows}
              onRowToggle={(e) => setExpandedRows(e.data)}
              rowExpansionTemplate={rowExpansionTemplate}
              removableSort
              sortField="registered_at"
              sortOrder={-1}
              showGridlines
              paginator
              rows={5}
              loading={loading}
              rowsPerPageOptions={[5, 10, 25, 50]}
              filters={filters}
              // filterDisplay="row"
              globalFilterFields={["Event.title"]}
              header={header}
              emptyMessage="No bookings found."
              onFilter={(e) => setFilters(e.filters)}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Bookings"
              tableStyle={{ minWidth: "50rem" }}
            >
              <Column expander={allowExpansion} style={{ width: "5rem" }} />
              <Column
                field="Event.title"
                header="Event"
                body={eventBodyTemplate}
                sortable
              ></Column>
              <Column
                headerStyle={{ width: "10%", minWidth: "8rem" }}
                bodyStyle={{ textAlign: "center" }}
                headerClassName="custom-status-column"
                header="QR Code"
                body={qrBodyTemplate}
              ></Column>
              <Column
                field="registered_at"
                header="Registered At"
                body={(rowData: Transaction) => rowData.registered_at_formatted}
                sortable
              ></Column>
              <Column
                headerStyle={{ width: "15%", minWidth: "8rem" }}
                field="amount"
                header="Amount Paid"
                bodyStyle={{ textAlign: "center" }}
                body={amountBodyTemplate}
                headerClassName="custom-status-column"
                sortable
              ></Column>
              <Column
                headerStyle={{ width: "10%", minWidth: "8rem" }}
                field="status_label"
                header="Registration Status"
                bodyStyle={{ textAlign: "center" }}
                body={statusBodyTemplate}
                headerClassName="custom-status-column"
              ></Column>
              <Column
                headerStyle={{ width: "15%", minWidth: "8rem" }}
                bodyStyle={{ textAlign: "center" }}
                headerClassName="custom-status-column"
                header="Actions"
                body={actionBodyTemplate}
              ></Column>
            </DataTable>
          </div>
        </div>
      </section>
      <Dialog
        visible={qrVisible}
        showHeader={true}
        header={`QR Code for ${selectedEvent || "Event"}`}
        position="center"
        onHide={() => {
          if (!qrVisible) return;
          setQrVisible(false);
        }}
        draggable={false}
        resizable={false}
      >
        <div className="col-12 d-flex align-items-center justify-content-center mt-3">
          {qrImage ? (
            <>
              <img
                src={qrImage}
                alt="QR Code"
                style={{ width: 300, height: 300 }}
              />
            </>
          ) : (
            <p>Generating QR...</p>
          )}
        </div>
        <div className="col-12 d-flex align-items-center justify-content-center mt-3">
          <p>Scan this QR code at the event entrance for check-in.</p>
        </div>
        <div className="col-12 d-flex align-items-center justify-content-center mt-3">
          <Button
            label="Close"
            outlined
            onClick={() => setQrVisible(false)}
            className="thm-black-btn small_btn"
          />
        </div>
      </Dialog>
    </>
  );
};

export default TransactionList;
